/**
 * Canonical Hub user-role model — the EMPLOYEE vs CLIENT distinction.
 *
 * Hub has exactly four roles, stored as `public.roles` rows and referenced by
 * `app_users.role_id` (there is NO `is_employee` / `is_client` boolean column —
 * the distinction is ENTIRELY derived from which role a user holds). This module
 * is the single typed source of truth for those role names + the classification
 * helpers so AI agents and humans stop re-deriving "is this person staff or a
 * client?" from ad-hoc string checks.
 *
 * | Role         | Class    | What they may do                                              |
 * |--------------|----------|---------------------------------------------------------------|
 * | `wtc_admin`  | employee | Everything. RLS `is_admin()` → sees ALL contacts/tickets/etc. |
 * | `wtc_crm`    | employee | CRM + selling. Per-contact access via `employee_contact_access`. |
 * | `wtc_employee` | employee | Standard CRM + tickets + time. Per-contact access via `employee_contact_access`. |
 * | `client_user` | client  | Limited self-service portal — sees ONLY their own data, gated by `contact_user_access`. |
 *
 * How access is actually enforced (Supabase RLS — see the migrations):
 *   - `is_admin()` / `is_employee()` / `is_client_user()` are SQL helpers keyed
 *     off the user's role name.
 *   - An ADMIN sees all contacts (no per-row grant needed).
 *   - A CRM/EMPLOYEE user sees a contact ONLY if a row exists in
 *     `employee_contact_access (user_id, contact_id)` — granular, per-contact.
 *   - A CLIENT_USER sees a contact ONLY if an active row exists in
 *     `contact_user_access (user_id, contact_id, is_active, expires_at)` — this
 *     is what scopes a client to their own company's tickets/communications.
 *
 * So "grant employee access" = set the role to an employee role (+ optionally
 * grant `employee_contact_access` for the contacts they should manage), and
 * "grant client access" = set the role to `client_user` + grant
 * `contact_user_access` to the contacts/company that are theirs.
 */

export const HUB_ROLE = {
  ADMIN: "wtc_admin",
  CRM: "wtc_crm",
  EMPLOYEE: "wtc_employee",
  CLIENT: "client_user",
} as const;

export type HubRoleName = (typeof HUB_ROLE)[keyof typeof HUB_ROLE];

/** Roles that are WTC STAFF (internal employees) — `is_employee()` in RLS. */
export const EMPLOYEE_ROLE_NAMES: readonly HubRoleName[] = [
  HUB_ROLE.ADMIN,
  HUB_ROLE.CRM,
  HUB_ROLE.EMPLOYEE,
];

/** Roles that are external CLIENTS using the limited self-service portal. */
export const CLIENT_ROLE_NAMES: readonly HubRoleName[] = [HUB_ROLE.CLIENT];

/** The employee roles a teammate may be assigned when granting employee access. */
export const ASSIGNABLE_EMPLOYEE_ROLES: readonly HubRoleName[] = [
  HUB_ROLE.ADMIN,
  HUB_ROLE.CRM,
  HUB_ROLE.EMPLOYEE,
];

export function isHubRoleName(value: unknown): value is HubRoleName {
  return (
    typeof value === "string" &&
    (Object.values(HUB_ROLE) as string[]).includes(value)
  );
}

/** True for any WTC-staff role (admin / crm / employee). */
export function isEmployeeRole(role: string | null | undefined): boolean {
  return !!role && (EMPLOYEE_ROLE_NAMES as readonly string[]).includes(role);
}

/** True for the external client portal role. */
export function isClientRole(role: string | null | undefined): boolean {
  return !!role && (CLIENT_ROLE_NAMES as readonly string[]).includes(role);
}

/** True only for the full-access admin role (`is_admin()` in RLS). */
export function isAdminRole(role: string | null | undefined): boolean {
  return role === HUB_ROLE.ADMIN;
}

/** Human-friendly display label for a role name. */
export const HUB_ROLE_LABELS: Record<HubRoleName, string> = {
  [HUB_ROLE.ADMIN]: "Admin (full access)",
  [HUB_ROLE.CRM]: "CRM user",
  [HUB_ROLE.EMPLOYEE]: "Employee",
  [HUB_ROLE.CLIENT]: "Client (portal)",
};

export function roleLabel(role: string | null | undefined): string {
  if (isHubRoleName(role)) return HUB_ROLE_LABELS[role];
  return role ?? "—";
}

/** One-line description of what each role can do (used in UI/Slack copy). */
export const HUB_ROLE_DESCRIPTIONS: Record<HubRoleName, string> = {
  [HUB_ROLE.ADMIN]:
    "Full system access — sees every contact, ticket, deal, and the CEO dashboard.",
  [HUB_ROLE.CRM]:
    "CRM + selling: contacts, deals, proposals, meetings. Per-contact access controlled by employee_contact_access.",
  [HUB_ROLE.EMPLOYEE]:
    "Standard staff: contacts, tickets, time tracking. Per-contact access controlled by employee_contact_access.",
  [HUB_ROLE.CLIENT]:
    "Limited client portal — sees ONLY their own tickets/communications, scoped by contact_user_access.",
};

/**
 * Coarse user class for a role name — the single concept most callers want
 * ("is this an internal staff member or an external client?").
 */
export type HubUserClass = "employee" | "client" | "unknown";

export function userClassForRole(role: string | null | undefined): HubUserClass {
  if (isEmployeeRole(role)) return "employee";
  if (isClientRole(role)) return "client";
  return "unknown";
}
